"use client";

import React from "react";
import { MapPin, ShieldCheck, Zap, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors font-sans";
function ClaimForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="92518" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-accent/20 dark:bg-accent/10 flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-accent-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground font-default">Audit Request Received</h3>
                <p className="text-muted-foreground max-w-md font-sans">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 font-sans">
                    Submit Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="full_name" rules={{
        required: "Administrator Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Practice Administrator Name</FormLabel>
                            <FormControl>
                                <input {...field} type="text" placeholder="Sarah Jenkins" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="font-sans">Enter the primary contact person's name.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email_address" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Practice Email Address</FormLabel>
                            <FormControl>
                                <input {...field} type="email" placeholder="admin@medicalpractice.com" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="font-sans">We'll send your practice audit report here.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Direct Contact Number <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                            <FormControl>
                                <input {...field} type="tel" placeholder="+1 (813) 214-0302" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="font-sans">Optional – for urgent clinical coding clarifications.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="business_name" rules={{
        required: "Practice Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Medical Practice Name</FormLabel>
                            <FormControl>
                                <input {...field} type="text" placeholder="Elite Healthcare Solutions" className={inputClasses} />
                            </FormControl>
                            <FormDescription className="font-sans">The name of the healthcare facility or clinic.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="business_sector" rules={{
        required: "Please select a medical specialty"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default">Medical Specialty</FormLabel>
                            <FormControl>
                                <select {...field} className={inputClasses}>
                                    <option value="" disabled>Select your specialty</option>
                                    {["General Practice / Family Medicine", "Cardiology", "Orthopedics", "Pediatrics", "Mental Health Services", "Other Specialty"].map((option, i) => <option key={i} value={option} data-index={i}>
                                            {option}
                                        </option>)}
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default">Primary Billing Challenges <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                            <FormControl>
                                <textarea {...field} placeholder="High denial rates, insurance credentialing issues, or RCM inefficiencies?" className={`${inputClasses} min-h-[100px] resize-y`} />
                            </FormControl>
                            <FormDescription className="font-sans">Briefly describe the financial bottlenecks you want us to audit.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-3 text-destructive">
                    <CircleAlert className="size-5 shrink-0 mt-0.5" />
                    <p className="text-sm font-medium font-sans">{submitError}</p>
                </div>}

            <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 sm:h-14 bg-gradient-to-r from-secondary to-primary hover:opacity-90 transition-opacity font-default">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Analyzing Practice Data...
                    </> : 'Request My Free Audit'}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-4 font-sans">
                By submitting this form, you agree to our HIPAA-compliant privacy policy. Confidentiality guaranteed.
            </p>
        </Form>;
}
export default function ClaimYourDigitalSpace() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="initiate-your-practice-audit" className="w-full py-16 md:py-24 lg:py-32 bg-background text-foreground border-t border-border relative overflow-hidden" ref={ref}>
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    
                    {}
                    <div className="lg:col-span-5 space-y-8">
                        <div className="space-y-4">
                            <Badge variant="secondary" className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-secondary/20 px-3 py-1 text-sm font-default">
                                <ShieldCheck className="size-4 mr-1.5" />
                                HIPAA Compliant Precision
                            </Badge>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
                                Initiate Your <span className="text-secondary">Practice Audit</span>
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-md font-sans">
                                Join the elite healthcare providers optimizing their revenue cycle. Get a comprehensive, data-driven billing and RCM audit to recover lost revenue.
                            </p>
                        </div>

                        <div className="space-y-6 pt-4 border-t border-border/50">
                            <div className="flex items-start gap-4">
                                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <MapPin className="size-5 text-primary" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-foreground font-default">Florida Headquarters</h4>
                                    <p className="text-muted-foreground text-sm mt-1 font-sans">
                                        7901 4th St N 27204<br />
                                        St. Petersburg, FL 33702<br />
                                        United States
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="size-10 rounded-lg bg-secondary/10 flex items-center justify-center shrink-0">
                                    <Zap className="size-5 text-secondary" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-foreground font-default">RCM Optimization</h4>
                                    <p className="text-muted-foreground text-sm mt-1 font-sans">
                                        Transitioning your practice from administrative burden to financial efficiency with expert coding and billing.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {}
                    <div className="lg:col-span-7">
                        <Card className="border-border/50 shadow-xl shadow-primary/5 bg-card/50 backdrop-blur-sm rounded-md">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-2xl font-default">Request Your Free Audit</CardTitle>
                                <CardDescription className="font-sans">
                                    Provide your details below and our specialists will perform a preliminary analysis of your billing performance.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FormProvider formKey="practice_audit_form_home" formId="practice_audit_form_home" sectionName="Initiate Your Practice Audit" defaultValues={{
                full_name: "",
                email_address: "",
                phone_number: "",
                business_name: "",
                business_sector: "",
                additional_details: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email_address: {
                  required: true,
                  pattern: "email"
                },
                business_name: {
                  required: true
                },
                business_sector: {
                  required: true
                }
              }} submitText="Request My Free Audit" successMessage="Thank you! Your audit request has been received. Our RCM specialists will contact you shortly with the preliminary findings.">
                                    <ClaimForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}